import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faSearch, faUsers, faTrash, faCheck, faGift, faSpinner } from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './PostEventContentPanel.css';

const PostEventContentPanel = ({ event, onClose }) => {
  const { getAccessToken } = useAuth();

  const [associations, setAssociations] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(true);

  const [showBundleSearch, setShowBundleSearch] = useState(false);
  const [allBundles, setAllBundles] = useState([]);
  const [bundleSearchQuery, setBundleSearchQuery] = useState('');
  const [loadingBundles, setLoadingBundles] = useState(false);

  const [showEligibleUsers, setShowEligibleUsers] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [showGrantConfirm, setShowGrantConfirm] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState(null);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchAssociations = useCallback(async () => {
    setLoadingAssociations(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/events/${event.id}/post-event-bundles`,
        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setAssociations(result.data || []);
      } else {
        setError(result.message || 'Failed to load associations');
      }
    } catch (err) {
      setError('Failed to load post-event bundles');
    } finally {
      setLoadingAssociations(false);
    }
  }, [event.id, getAccessToken]);

  useEffect(() => {
    fetchAssociations();
  }, [fetchAssociations]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchBundles = async () => {
    setLoadingBundles(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/content/bundles?activeOnly=true`,
        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      setAllBundles(Array.isArray(result) ? result : (result.data || []));
    } catch (err) {
      setError('Failed to load bundles');
    } finally {
      setLoadingBundles(false);
    }
  };

  const handleOpenBundleSearch = () => {
    setShowBundleSearch(true);
    setBundleSearchQuery('');
    fetchBundles();
  };

  const handleAssociateBundle = async (bundleId) => {
    setError('');
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/events/${event.id}/post-event-bundles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ bundleId })
        }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setSuccessMessage('Bundle associated successfully');
        setShowBundleSearch(false);
        fetchAssociations();
      } else {
        setError(result.message || 'Failed to associate bundle');
      }
    } catch (err) {
      setError('Failed to associate bundle');
    }
  };

  const handleRemoveAssociation = async (bundleId) => {
    setError('');
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/events/${event.id}/post-event-bundles/${bundleId}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setSuccessMessage('Bundle association removed');
        fetchAssociations();
      } else {
        setError(result.message || 'Failed to remove association');
      }
    } catch (err) {
      setError('Failed to remove association');
    }
  };

  const handleViewEligibleUsers = async () => {
    setShowEligibleUsers(true);
    setLoadingUsers(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/events/${event.id}/eligible-users`,
        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setEligibleUsers(result.data || []);
      } else {
        setError(result.message || 'Failed to load eligible users');
      }
    } catch (err) {
      setError('Failed to load eligible users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpenGrantConfirm = (assoc) => {
    setSelectedAssociation(assoc);
    setShowGrantConfirm(true);
    setExpiresAt('');
    setGrantResult(null);
  };

  const handleGrantAccess = async () => {
    setGranting(true);
    setError('');
    try {
      const token = await getAccessToken();
      const body = {};
      if (expiresAt) {
        body.expiresAt = new Date(expiresAt).toISOString().replace('Z', '');
      }
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/events/${event.id}/post-event-bundles/${selectedAssociation.bundleId}/grant-access`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body)
        }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setGrantResult(result.data);
        setSuccessMessage(`Access granted to ${result.data.totalUsersGranted} users`);
        fetchAssociations();
      } else {
        setError(result.message || 'Failed to grant access');
      }
    } catch (err) {
      setError('Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  const filteredBundles = allBundles.filter(bundle => {
    if (!bundleSearchQuery) return true;
    const query = bundleSearchQuery.toLowerCase();
    return (
      bundle.title?.toLowerCase().includes(query) ||
      bundle.description?.toLowerCase().includes(query)
    );
  });

  const alreadyAssociatedIds = new Set(associations.map(a => a.bundleId));

  return (
    <div className="post-event-overlay" onClick={onClose}>
      <div className="post-event-panel" onClick={(e) => e.stopPropagation()}>
        <div className="post-event-header">
          <div>
            <h3><FontAwesomeIcon icon={faGift} /> Post-Event Content</h3>
            <p className="post-event-event-name">{event.name}</p>
          </div>
          <button className="close-modal-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="post-event-body">
          {error && (
            <div className="post-event-error">
              {error}
              <button onClick={() => setError('')}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
          )}
          {successMessage && (
            <div className="post-event-success">{successMessage}</div>
          )}

          {/* Associated Bundles Section */}
          <div className="post-event-section">
            <div className="post-event-section-header">
              <h4>Associated Bundles</h4>
              <button className="btn-primary-sm" onClick={handleOpenBundleSearch}>
                <FontAwesomeIcon icon={faPlus} /> Associate Bundle
              </button>
            </div>

            {loadingAssociations ? (
              <div className="post-event-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
            ) : associations.length === 0 ? (
              <p className="post-event-empty">No bundles associated with this event yet.</p>
            ) : (
              <div className="post-event-bundle-list">
                {associations.map(assoc => (
                  <div key={assoc.id} className={`post-event-bundle-card ${assoc.accessGranted ? 'granted' : ''}`}>
                    <div className="bundle-card-info">
                      {assoc.bundleThumbnailUrl && (
                        <img src={assoc.bundleThumbnailUrl} alt="" className="bundle-thumb" />
                      )}
                      <div>
                        <strong>{assoc.bundleTitle || `Bundle #${assoc.bundleId}`}</strong>
                        {assoc.bundleDescription && (
                          <p className="bundle-desc">{assoc.bundleDescription}</p>
                        )}
                        <div className="bundle-meta">
                          {assoc.accessGranted ? (
                            <span className="badge badge-granted">
                              <FontAwesomeIcon icon={faCheck} /> Granted to {assoc.grantedUserCount} users
                            </span>
                          ) : (
                            <span className="badge badge-pending">Pending</span>
                          )}
                          {assoc.expiresAt && (
                            <span className="bundle-expires">Expires: {new Date(assoc.expiresAt).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bundle-card-actions">
                      {!assoc.accessGranted && (
                        <>
                          <button
                            className="btn-grant"
                            onClick={() => handleOpenGrantConfirm(assoc)}
                            title="Grant access to eligible users"
                          >
                            <FontAwesomeIcon icon={faCheck} /> Allow Access
                          </button>
                          <button
                            className="btn-remove"
                            onClick={() => handleRemoveAssociation(assoc.bundleId)}
                            title="Remove association"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Eligible Users Button */}
          <div className="post-event-section">
            <button className="btn-secondary-full" onClick={handleViewEligibleUsers}>
              <FontAwesomeIcon icon={faUsers} /> View Eligible Users ({event.isVirtualEvent ? 'Ticket Holders + FCY Virtual' : 'Ticket Holders + FCY Audit'})
            </button>
          </div>
        </div>

        {/* Bundle Search Modal */}
        {showBundleSearch && (
          <div className="post-event-sub-overlay" onClick={() => setShowBundleSearch(false)}>
            <div className="post-event-sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h4><FontAwesomeIcon icon={faSearch} /> Search Bundles</h4>
                <button className="close-modal-button" onClick={() => setShowBundleSearch(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="sub-modal-body">
                <div className="search-input-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search bundles by title..."
                    value={bundleSearchQuery}
                    onChange={(e) => setBundleSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
                {loadingBundles ? (
                  <div className="post-event-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading bundles...</div>
                ) : filteredBundles.length === 0 ? (
                  <p className="post-event-empty">No bundles found.</p>
                ) : (
                  <div className="bundle-search-results">
                    {filteredBundles.map(bundle => (
                      <div key={bundle.id} className="bundle-search-item">
                        <div className="bundle-search-info">
                          {bundle.thumbnailUrl && <img src={bundle.thumbnailUrl} alt="" className="bundle-thumb-sm" />}
                          <div>
                            <strong>{bundle.title}</strong>
                            {bundle.description && <p>{bundle.description}</p>}
                            <span className="bundle-type-badge">{bundle.type}</span>
                          </div>
                        </div>
                        {alreadyAssociatedIds.has(bundle.id) ? (
                          <span className="already-associated">Already Associated</span>
                        ) : (
                          <button
                            className="btn-primary-sm"
                            onClick={() => handleAssociateBundle(bundle.id)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> Add
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Eligible Users Modal */}
        {showEligibleUsers && (
          <div className="post-event-sub-overlay" onClick={() => setShowEligibleUsers(false)}>
            <div className="post-event-sub-modal wide" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h4><FontAwesomeIcon icon={faUsers} /> Eligible Users ({eligibleUsers.length})</h4>
                <button className="close-modal-button" onClick={() => setShowEligibleUsers(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="sub-modal-body">
                {loadingUsers ? (
                  <div className="post-event-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading users...</div>
                ) : eligibleUsers.length === 0 ? (
                  <p className="post-event-empty">No eligible users found for this event.</p>
                ) : (
                  <div className="eligible-users-table-wrapper">
                    <table className="eligible-users-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>IBO Number</th>
                          <th>Name</th>
                          <th>Team Name</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleUsers.map((user, idx) => (
                          <tr key={user.iboNumber}>
                            <td>{idx + 1}</td>
                            <td>{user.iboNumber}</td>
                            <td>{user.name || '-'}</td>
                            <td>{user.teamName || '-'}</td>
                            <td>
                              <span className={`source-badge ${user.source?.toLowerCase()}`}>
                                {user.source === 'TICKET_PURCHASE' ? 'Ticket' : 
                                 user.source === 'FCY_VIRTUAL' ? 'FCY (Virtual)' : 
                                 user.source === 'FCY_AUDIT' ? 'FCY (Audit)' : user.source}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grant Access Confirmation Modal */}
        {showGrantConfirm && selectedAssociation && (
          <div className="post-event-sub-overlay" onClick={() => { setShowGrantConfirm(false); setGrantResult(null); }}>
            <div className="post-event-sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h4><FontAwesomeIcon icon={faCheck} /> Grant Access</h4>
                <button className="close-modal-button" onClick={() => { setShowGrantConfirm(false); setGrantResult(null); }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="sub-modal-body">
                {grantResult ? (
                  <div className="grant-result">
                    <div className="grant-result-icon">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <h4>Access Granted Successfully</h4>
                    <div className="grant-result-details">
                      <p><strong>Bundle:</strong> {grantResult.bundleTitle}</p>
                      <p><strong>Users Granted:</strong> {grantResult.totalUsersGranted}</p>
                      {grantResult.skippedAlreadyHaveAccess > 0 && (
                        <p><strong>Skipped (already have access):</strong> {grantResult.skippedAlreadyHaveAccess}</p>
                      )}
                      <p><strong>Expires:</strong> {grantResult.expiresAt ? new Date(grantResult.expiresAt).toLocaleDateString() : 'Permanent (no expiry)'}</p>
                    </div>
                    <button className="btn-primary-full" onClick={() => { setShowGrantConfirm(false); setGrantResult(null); }}>
                      Done
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="grant-confirm-text">
                      You are about to grant access to <strong>{selectedAssociation.bundleTitle}</strong> for
                      all eligible users of <strong>{event.name}</strong>.
                    </p>
                    <div className="grant-form-group">
                      <label>Access Expiry Date <span className="optional-label">(optional)</span></label>
                      <input
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      <p className="grant-form-helper">Leave empty for permanent access</p>
                    </div>
                    <div className="grant-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => { setShowGrantConfirm(false); setGrantResult(null); }}
                        disabled={granting}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-grant-confirm"
                        onClick={handleGrantAccess}
                        disabled={granting}
                      >
                        {granting ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> Granting...</>
                        ) : (
                          <><FontAwesomeIcon icon={faCheck} /> Allow Access</>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostEventContentPanel;
